import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { catchError, finalize, tap } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import { ScheduledNotificationsComponent } from "../shared/scheduled-notifications/scheduled-notifications.component";
import { ServerTokenService } from "../../services/server-token.service";
import { FormFieldOnChangeEvent } from "../../interface";
import {
  AdminInferenceValue,
  AdvancedDirectiveSettings,
} from "./advanced-directive-settings.interface";
import {
  DNI_ONLY_CPR_CONFIG,
  DNR_ONLY_INTUBATION_CONFIG,
  ERRORS,
  SUCCESS_MSG,
} from "./advanced-directive-settings.constants";
import {
  AD_REPORT_ID,
  AD_REPORT_TYPE_WIRE,
} from "../report-notifications.types";

@Component({
  selector: 'advanced-directive-settings',
  templateUrl: './advanced-directive-settings.component.html',
  styleUrls: ['./advanced-directive-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedDirectiveSettingsComponent implements OnInit {

  settings: { dnrOnly_intubation: AdminInferenceValue; dniOnly_cpr: AdminInferenceValue } = {
    dnrOnly_intubation: 'Empty',
    dniOnly_cpr: 'Empty',
  };

  generalError = '';
  successMsg = '';
  dataLoading = false;
  saving = false;

  readonly DNR_ONLY_INTUBATION_CONFIG = DNR_ONLY_INTUBATION_CONFIG;
  readonly DNI_ONLY_CPR_CONFIG = DNI_ONLY_CPR_CONFIG;
  readonly AD_REPORT_ID = AD_REPORT_ID;
  readonly AD_REPORT_TYPE_WIRE = AD_REPORT_TYPE_WIRE;

  @ViewChild(ScheduledNotificationsComponent) scheduledNotifications?: ScheduledNotificationsComponent;

  constructor(
    private server: ServerTokenService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.loadSettings();
  }

  onDnrOnlyIntubationChange({ value }: FormFieldOnChangeEvent): void {
    this.settings = { ...this.settings, dnrOnly_intubation: value as AdminInferenceValue };
  }

  onDniOnlyCprChange({ value }: FormFieldOnChangeEvent): void {
    this.settings = { ...this.settings, dniOnly_cpr: value as AdminInferenceValue };
  }

  onSave(): void {
    if (this.saving) return;
    this.generalError = '';
    this.successMsg = '';

    // save() rejects synchronously while the schedule is loading or after a
    // failed load; inside the forkJoin that would abort the settings POST.
    if (this.scheduledNotifications?.scheduleLoading || this.scheduledNotifications?.scheduleLoadError) {
      this.generalError = ERRORS.SCHEDULE_NOT_READY;
      this.cdr.markForCheck();
      return;
    }

    this.saving = true;

    forkJoin({
      settings: this.server.postWithToken<AdvancedDirectiveSettings>(
        'snfs/admin/setAdvancedDirectiveSettings',
        this.settings,
      ),
      schedule: this.scheduledNotifications ? this.scheduledNotifications.save() : of(null),
    }).pipe(
      tap(({ settings: s }) => {
        this.settings = {
          dnrOnly_intubation: s.dnrOnly_intubation,
          dniOnly_cpr: s.dniOnly_cpr,
        };
        this.successMsg = SUCCESS_MSG;
        setTimeout(() => {
          this.successMsg = '';
          this.cdr.markForCheck();
        }, 3000);
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.SAVE;
        return of(null);
      }),
      finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }),
    ).subscribe();
  }

  private loadSettings(): void {
    this.dataLoading = true;
    this.server.getWithToken<AdvancedDirectiveSettings>(
      'snfs/admin/getAdvancedDirectiveSettings',
    ).pipe(
      tap(response => {
        if (response) {
          this.settings = {
            dnrOnly_intubation: response.dnrOnly_intubation ?? 'Empty',
            dniOnly_cpr: response.dniOnly_cpr ?? 'Empty',
          };
        }
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.GET;
        return of(null);
      }),
      finalize(() => {
        this.dataLoading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe();
  }
}
